import { BASE_PLAN_TYPES, BILLING_CYCLE } from "@/common/constants";
import { orderBy } from 'lodash';

/**
 * usage:
 *
 * plansof(planMapFlat).type('zoom_meeting').annual().get();
 *
 * plansof(planMapFlat).monthly().saas().capacity(100).get();
 *
 * plansof(selectedList).annual().sum('license');
 *
 * plansof(selectedList).annual().sum('amount');
 *
 * plansof(planMapFlat, selectedList).type('pbx', 'zoom_meeting').not().sub_type('PN').yes().annual().get();
 *
 * - remember to stop 'not' with 'yes'
 *
 */

class PlansofPrototype {
    plans = []
    __reverse = false;
    constructor(...plans) {
        plans = plans.flat();
        this.plans = [...plans];
    }
    __maybeReverse(is) {
        if (this.__reverse) return !is;
        return is;
    }
    // raw filter methods
    filter(fn) {
        this.plans = this.plans.filter(fn);
        return this;
    }
    // order desc
    desc(...keys){
        this.plans = orderBy(this.plans, keys, 'desc');
        return this;
    }
    // order asc
    asc(...keys){
        this.plans = orderBy(this.plans, keys, 'asc');
        return this;
    }
    // assertive
    yes() {
        this.__reverse = false;
        return this;
    }
    // reversed assertive
    not() {
        this.__reverse = true;
        return this;
    }
    // dump
    get(index) {
        if (!Number.isInteger(index)) return this.plans || [];
        const length = this.plans.length;
        const idx = (index % length + length) % length; // quick support for getting first and last: get(0), get(-1);
        return this.plans?.[idx] ?? {};
    }
    // boolean
    has(number) {
        if (!Number.isInteger(number)) return !!this.plans.length;
        return this.plans.length === number;
    }
    // clone
    clone() {
        return new Plansof(this.plans);
    }
    // sum
    sum(key) {
        if (key === 'license' || key === 0) {
            return this.plans.reduce((acc, x) => acc + x.cartInfo?.selected_number ?? 0, 0);
        } else if (key === 'amount' || key === 1) {
            return this.plans.reduce((acc, x) => acc + (x.cartInfo?.selected_number ?? 0) * x.amount, 0);
        } else if (key === 'type' || key === 2) {
            const types = this.plans.reduce((acc, x) => (acc.push(x.type), acc), []);
            return [...new Set(types)];
        } else if (key === 'price' || key === 3) {
            return this.plans.reduce((acc, x) => acc + 1 * x.amount, 0);
        }
    }
};

class Plansof extends PlansofPrototype {
    constructor(...args) {
        super(...args)
    }
    base() {
        this.plans = this.plans.filter(x => this.__maybeReverse(BASE_PLAN_TYPES.includes(x.type)));
        return this;
    }
    type(...types) {
        this.plans = this.plans.filter(x => this.__maybeReverse(types.includes(x.type)));
        return this;
    }
    code(...codes) {
        this.plans = this.plans.filter(x => this.__maybeReverse(codes.includes(x.plan_code)));
        return this;
    }
    sub_type(...types) {
        this.plans = this.plans.filter(x => this.__maybeReverse(types.includes(x.sub_type)));
        return this;
    }
    category(...categories) {
        this.plans = this.plans.filter(x => this.__maybeReverse(categories.includes(x.category)));
        return this;
    }
    parentCategory(...parentCategories) {
        this.plans = this.plans.filter(x => this.__maybeReverse(parentCategories.includes(x.parent_category)));
        return this;
    }
    capacity(...capacities) {
        this.plans = this.plans.filter(x => this.__maybeReverse(capacities.includes(x.capacity)));
        return this;
    }
    saas() {
        this.plans = this.plans.filter(x => this.__maybeReverse(x.isSaas));
        return this;
    }
    region(...regions) {
        this.plans = this.plans.filter(x => this.__maybeReverse(regions.includes(x.region)));
        return this;
    }
    freeTrial() {
        this.plans = this.plans.filter(x => this.__maybeReverse(!!x.isOnlineFreeTrial));
        return this;
    }
    cycle(...cycles) {
        this.plans = this.plans.filter(x => this.__maybeReverse(cycles.includes(x.billing_cycle)));
        return this;
    }
    monthly() {
        return this.cycle(BILLING_CYCLE.MONTHLY);
    }
    one_month() {
        return this.cycle(BILLING_CYCLE.ONE_MONTH);
    }
    annual() {
        return this.cycle(BILLING_CYCLE.ANNUAL);
    }
    quarterly() {
      return this.cycle(BILLING_CYCLE.QUARTERLY);
    }
    twoYears() {
        return this.cycle(BILLING_CYCLE.TWO_YEARS);
    }
    threeYears() {
        return this.cycle(BILLING_CYCLE.THREE_YEARS);
    }
}

export const plansof = (plans) => new Plansof(plans);
